import * as THREE from 'three';
import type { World } from '../sim/world';
import { hostile } from '../sim/captain';
import type { Input } from '../ui/input';
import { DECK, DECK_X_MAX, DECK_X_MIN, WHEEL, deckHalfWidth, deckY, cannonMount, CANNONS, sheerY } from '../data/ship';
import { fbm1 } from '../core/noise';

export type CameraMode = 'orbit' | 'free' | 'deck' | 'helm' | 'cinematic' | 'wide' | 'detail' | 'battle';

export const CAMERA_MODES: readonly { id: CameraMode; name: string; key: string }[] = [
  { id: 'orbit', name: 'Third person', key: '1' },
  { id: 'free', name: 'Free camera', key: '2' },
  { id: 'deck', name: 'On deck', key: '3' },
  { id: 'helm', name: 'At the helm', key: '4' },
  { id: 'cinematic', name: 'Cinematic follow', key: '5' },
  { id: 'wide', name: 'Wide ocean', key: '6' },
  { id: 'detail', name: 'Close-up details', key: '7' },
  { id: 'battle', name: 'Battle view', key: '8' },
];

interface Shot {
  name: string;
  duration: number;
  /** Returns camera position & target (world) at shot time t. */
  eval: (t: number, ctx: ShotContext, pos: THREE.Vector3, target: THREE.Vector3) => { fov: number; focus?: number; dof?: number };
}

interface ShotContext {
  ship: THREE.Object3D;
  world: World;
  seed: number;
}

const tmpV = new THREE.Vector3();
const tmpQ = new THREE.Quaternion();
const UP = new THREE.Vector3(0, 1, 0);

function local(ship: THREE.Object3D, x: number, y: number, z: number, out: THREE.Vector3): THREE.Vector3 {
  return out.set(x, y, z).applyMatrix4(ship.matrixWorld);
}

/** Point relative to the ship's position but in a heading-only frame (no roll/pitch). */
function level(ctx: ShotContext, x: number, y: number, z: number, out: THREE.Vector3): THREE.Vector3 {
  const h = ctx.world.ship.heading;
  const fx = Math.sin(h), fz = -Math.cos(h);
  const sx = -fz, sz = fx;
  const o = ctx.ship.position;
  return out.set(o.x + fx * x + sx * z, y, o.z + fz * x + sz * z);
}

const CINEMATIC: Shot[] = [
  {
    name: 'Tracking, low on the water',
    duration: 14,
    eval: (t, c, p, tg) => {
      level(c, 10 - t * 1.6, 1.8, 34 + Math.sin(t * 0.2) * 3, p);
      local(c.ship, 2, 8, 0, tg);
      return { fov: 42, focus: 34, dof: 0.35 };
    },
  },
  {
    name: 'Chase from astern',
    duration: 13,
    eval: (t, c, p, tg) => {
      level(c, -70 + t * 0.8, 14 + Math.sin(t * 0.3) * 2, -14 + t * 1.5, p);
      local(c.ship, 8, 12, 0, tg);
      return { fov: 38, focus: 70, dof: 0.2 };
    },
  },
  {
    name: 'Bow wave',
    duration: 12,
    eval: (t, c, p, tg) => {
      level(c, 42 - t * 0.4, 3.2, -16 - t * 0.3, p);
      local(c.ship, 14, 3, 0, tg);
      return { fov: 34, focus: 32, dof: 0.35 };
    },
  },
  {
    name: 'Crane over the quarter',
    duration: 15,
    eval: (t, c, p, tg) => {
      level(c, -30 + t * 1.2, 6 + t * 2.6, 26 - t * 0.6, p);
      local(c.ship, 0, 14, 0, tg);
      return { fov: 44, focus: 42, dof: 0.25 };
    },
  },
  {
    name: 'Under the sails',
    duration: 12,
    eval: (t, c, p, tg) => {
      local(c.ship, -2 + t * 0.35, DECK.mainY + 1.2, 3.2, p);
      local(c.ship, 3, 26, -2, tg);
      return { fov: 64, focus: 18, dof: 0.2 };
    },
  },
];

const WIDE: Shot[] = [
  {
    name: 'Horizon',
    duration: 18,
    eval: (t, c, p, tg) => {
      level(c, 260 - t * 3, 18, 420, p);
      local(c.ship, 0, 12, 0, tg);
      return { fov: 22, focus: 480, dof: 0.05 };
    },
  },
  {
    name: 'High above',
    duration: 18,
    eval: (t, c, p, tg) => {
      const a = 2.2 + t * 0.018;
      level(c, Math.cos(a) * 360, 150 + t * 1.5, Math.sin(a) * 360, p);
      local(c.ship, 0, 6, 0, tg);
      return { fov: 26, focus: 390, dof: 0.05 };
    },
  },
  {
    name: 'Sea level, far off',
    duration: 18,
    eval: (t, c, p, tg) => {
      level(c, -520 + t * 2, 3.5, -300, p);
      local(c.ship, 0, 14, 0, tg);
      return { fov: 16, focus: 600, dof: 0.05 };
    },
  },
  {
    name: 'Ahead, closing',
    duration: 16,
    eval: (t, c, p, tg) => {
      level(c, 520 - t * 6, 12, 60, p);
      local(c.ship, 0, 14, 0, tg);
      return { fov: 20, focus: 500, dof: 0.05 };
    },
  },
];

const gun = CANNONS[4]!;
const gunM = cannonMount(gun);

const DETAIL: Shot[] = [
  {
    name: 'Figurehead and bow',
    duration: 10,
    eval: (t, c, p, tg) => {
      local(c.ship, 23 - t * 0.15, 3.5, -5.5 + t * 0.2, p);
      local(c.ship, 18.8, 3.2, 0, tg);
      return { fov: 38, focus: 6.5, dof: 0.9 };
    },
  },
  {
    name: 'Great guns',
    duration: 10,
    eval: (t, c, p, tg) => {
      local(c.ship, gunM.x + 2.8 - t * 0.12, gunM.y + 1.0, gunM.z * 0.35, p);
      local(c.ship, gunM.x, gunM.y, gunM.z, tg);
      return { fov: 42, focus: 3.4, dof: 1 };
    },
  },
  {
    name: 'The wheel',
    duration: 10,
    eval: (t, c, p, tg) => {
      local(c.ship, WHEEL.x - 2.6 + t * 0.08, DECK.quarterY + 1.9, 1.8 - t * 0.12, p);
      local(c.ship, WHEEL.x, DECK.quarterY + 1.1, 0, tg);
      return { fov: 40, focus: 3.1, dof: 1 };
    },
  },
  {
    name: 'Shrouds and deadeyes',
    duration: 10,
    eval: (t, c, p, tg) => {
      local(c.ship, 1.5 - t * 0.1, sheerY(1) + 0.9, 5.2, p);
      local(c.ship, 0.4, sheerY(1) + 3.5, 6.1, tg);
      return { fov: 46, focus: 3, dof: 1 };
    },
  },
  {
    name: 'Stern lanterns',
    duration: 10,
    eval: (t, c, p, tg) => {
      local(c.ship, -22.5 + t * 0.1, sheerY(-18) + 2.4, 4.5 - t * 0.35, p);
      local(c.ship, -18.3, sheerY(-18.3) + 1.2, 0, tg);
      return { fov: 40, focus: 5.5, dof: 0.9 };
    },
  },
  {
    name: 'Canvas aloft',
    duration: 10,
    eval: (t, c, p, tg) => {
      local(c.ship, 4.5, 20.5, 1.5 + t * 0.1, p);
      local(c.ship, 1.5, 30, -2, tg);
      return { fov: 50, focus: 9, dof: 0.6 };
    },
  },
];

export class CameraRig {
  readonly camera: THREE.PerspectiveCamera;
  mode: CameraMode = 'orbit';
  shotName = '';
  focus = 40;
  dofAmount = 0.2;

  // Orbit
  private yaw = 2.4;
  private pitch = 0.18;
  private distance = 62;
  private readonly orbitCenter = new THREE.Vector3();
  // Free
  private readonly freePos = new THREE.Vector3();
  private freeYaw = 0;
  private freePitch = 0;
  // Deck walk (ship-local)
  private deckX = 4;
  private deckZ = 1.5;
  private lookYaw = 0;
  private lookPitch = 0;
  // Battle view: the ship it frames, and the player's swing around it.
  targetId = -1;
  private battleYaw = 0;
  private battleDist = 1;
  // Auto shots
  private shotIndex = 0;
  private shotTime = 0;

  private readonly pos = new THREE.Vector3();
  private readonly target = new THREE.Vector3();
  private readonly smoothPos = new THREE.Vector3();
  private readonly smoothTarget = new THREE.Vector3();
  private fov = 50;
  private shakeAmp = 0;
  private shakeTime = 0;
  private snap = true;

  constructor(aspect: number) {
    this.camera = new THREE.PerspectiveCamera(50, aspect, 0.25, 50000);
  }

  setMode(m: CameraMode, input: Input): void {
    if (m === this.mode) return;
    if (m === 'free') {
      this.freePos.copy(this.camera.position);
      const d = this.camera.getWorldDirection(tmpV);
      this.freeYaw = Math.atan2(-d.x, -d.z);
      this.freePitch = Math.asin(THREE.MathUtils.clamp(d.y, -1, 1));
    }
    if (m === 'deck' || m === 'helm') {
      this.lookYaw = 0;
      this.lookPitch = m === 'helm' ? 0.05 : 0;
    }
    this.mode = m;
    this.shotIndex = 0;
    this.shotTime = 0;
    this.snap = true;
    if (m === 'free' || m === 'deck') input.requestLock();
    else input.releaseLock();
  }

  /** Frame the next ship our lookouts have in sight (nearest hostile first). */
  cycleTarget(world: World): void {
    const me = world.player;
    const list = world.vessels
      .filter((v) => v !== me && v.state !== 'sunk' && world.lookout.get(v.id)?.visible)
      .map((v) => ({ v, d: Math.hypot(v.ship.com.x - me.ship.com.x, v.ship.com.z - me.ship.com.z) - (hostile(v, me) ? 5000 : 0) }))
      .sort((a, b) => a.d - b.d);
    if (!list.length) {
      this.targetId = -1;
      return;
    }
    const i = list.findIndex((e) => e.v.id === this.targetId);
    this.targetId = list[(i + 1) % list.length]!.v.id;
  }

  shake(amount: number): void {
    this.shakeAmp = Math.min(1.5, this.shakeAmp + amount);
  }

  update(dt: number, world: World, ship: THREE.Object3D, input: Input): void {
    const mdx = input.mouseDX * 0.0025, mdy = input.mouseDY * 0.0025;
    let focus = 40, dof = 0.15, fov = 50;
    let stabilise = 1;
    let rollFollow = 0;
    let posRate = 6, targetRate = 8;

    switch (this.mode) {
      case 'orbit': {
        this.yaw -= mdx;
        this.pitch = THREE.MathUtils.clamp(this.pitch + mdy, -0.08, 1.35);
        this.distance = THREE.MathUtils.clamp(this.distance * Math.pow(1.12, input.wheel), 14, 260);
        // Follow the ship's centre with some lag so she feels heavy.
        local(ship, 0, 9, 0, tmpV);
        this.orbitCenter.lerp(tmpV, this.snap ? 1 : 1 - Math.exp(-dt * 2.5));
        const h = world.ship.heading;
        const a = this.yaw + (Math.PI / 2 - h);
        const cp = Math.cos(this.pitch);
        this.pos.set(
          this.orbitCenter.x + Math.cos(a) * cp * this.distance,
          this.orbitCenter.y + Math.sin(this.pitch) * this.distance,
          this.orbitCenter.z - Math.sin(a) * cp * this.distance,
        );
        this.target.copy(this.orbitCenter);
        focus = this.distance;
        dof = 0.18;
        fov = 50;
        rollFollow = 0.08;
        posRate = 7;
        this.shotName = '';
        break;
      }
      case 'battle': {
        // Over our quarter, looking across at the ship we're fighting.
        // Only a ship the lookouts can see; the camera doesn't know more than they do.
        let other = world.vessels.find((v) => v.id === this.targetId && v.state !== 'sunk' && world.lookout.get(v.id)?.visible);
        if (!other) {
          this.cycleTarget(world);
          other = world.vessels.find((v) => v.id === this.targetId);
        }
        local(ship, 0, 9, 0, tmpV);
        this.orbitCenter.lerp(tmpV, this.snap ? 1 : 1 - Math.exp(-dt * 2.5));
        this.battleYaw = THREE.MathUtils.clamp(this.battleYaw - mdx, -1.2, 1.2);
        this.battleDist = THREE.MathUtils.clamp(this.battleDist * Math.pow(1.1, input.wheel), 0.5, 2.5);
        if (!other) {
          this.pos.copy(this.orbitCenter).add(new THREE.Vector3(40, 20, 40));
          this.target.copy(this.orbitCenter);
          this.shotName = 'No ships in sight';
          break;
        }
        const e = new THREE.Vector3(other.ship.com.x, other.ship.com.y + 8 * other.ship.sy, other.ship.com.z);
        const dx = this.orbitCenter.x - e.x, dz = this.orbitCenter.z - e.z;
        const d = Math.hypot(dx, dz) || 1;
        const a = Math.atan2(dz, dx) + this.battleYaw * 0.8 + 0.35;
        const back = (38 + Math.min(d, 1500) * 0.06) * this.battleDist;
        this.pos.set(this.orbitCenter.x + Math.cos(a) * back, this.orbitCenter.y + 10 + back * 0.28, this.orbitCenter.z + Math.sin(a) * back);
        this.target.copy(this.orbitCenter).lerp(e, Math.min(0.75, 0.35 + d / 3000));
        fov = THREE.MathUtils.clamp(28 + 20000 / (d + 400), 30, 55);
        focus = this.pos.distanceTo(e);
        dof = 0.04;
        posRate = 4;
        targetRate = 5;
        rollFollow = 0.04;
        this.shotName = `${other.name} · ${other.cls.name} · ${Math.round(d)} m`;
        break;
      }
      case 'free': {
        this.freeYaw -= mdx;
        this.freePitch = THREE.MathUtils.clamp(this.freePitch - mdy, -1.5, 1.5);
        const fwd = tmpV.set(-Math.sin(this.freeYaw) * Math.cos(this.freePitch), Math.sin(this.freePitch), -Math.cos(this.freeYaw) * Math.cos(this.freePitch));
        const right = new THREE.Vector3(Math.cos(this.freeYaw), 0, -Math.sin(this.freeYaw));
        const speed = (input.isDown('ShiftLeft') ? 60 : 14) * dt;
        const f = input.axis(['KeyS'], ['KeyW']), s = input.axis(['KeyA'], ['KeyD']), u = input.axis(['KeyF'], ['KeyR', 'Space']);
        this.freePos.addScaledVector(fwd, f * speed).addScaledVector(right, s * speed);
        this.freePos.y += u * speed;
        this.pos.copy(this.freePos);
        this.target.copy(this.freePos).add(fwd);
        focus = 30;
        dof = 0.08;
        fov = 55;
        posRate = 30;
        targetRate = 30;
        this.shotName = '';
        break;
      }
      case 'deck':
      case 'helm': {
        this.lookYaw -= mdx;
        this.lookPitch = THREE.MathUtils.clamp(this.lookPitch - mdy, -1.2, 1.3);
        let x: number, z: number, eye: number;
        if (this.mode === 'deck') {
          const f = input.axis(['KeyS'], ['KeyW']), s = input.axis(['KeyA'], ['KeyD']);
          const sp = (input.isDown('ShiftLeft') ? 3.2 : 1.6) * dt;
          this.deckX += (Math.cos(this.lookYaw) * f + Math.sin(this.lookYaw) * s) * sp;
          this.deckZ += (-Math.sin(this.lookYaw) * f + Math.cos(this.lookYaw) * s) * sp;
          this.deckX = THREE.MathUtils.clamp(this.deckX, DECK_X_MIN + 1.2, DECK_X_MAX - 1.6);
          const hw = Math.max(0.3, deckHalfWidth(this.deckX) - 0.35);
          this.deckZ = THREE.MathUtils.clamp(this.deckZ, -hw, hw);
          x = this.deckX;
          z = this.deckZ;
          eye = 1.68;
          this.shotName = 'WASD to walk';
        } else {
          this.lookYaw = THREE.MathUtils.clamp(this.lookYaw, -1.9, 1.9);
          x = WHEEL.x - 1.45;
          z = 0;
          eye = 1.72;
          this.shotName = '';
        }
        const y = deckY(x) + eye;
        // Head bob when walking.
        const bob = this.mode === 'deck' ? Math.sin(world.time * 7) * 0.02 * (input.axis(['KeyS'], ['KeyW']) !== 0 ? 1 : 0) : 0;
        local(ship, x, y + bob, z, this.pos);
        const dir = new THREE.Vector3(Math.cos(this.lookYaw) * Math.cos(this.lookPitch), Math.sin(this.lookPitch), -Math.sin(this.lookYaw) * Math.cos(this.lookPitch));
        local(ship, x + dir.x * 10, y + dir.y * 10, z + dir.z * 10, this.target);
        // People on deck partly compensate for roll with their heads.
        stabilise = 0.45;
        focus = 12;
        dof = 0.05;
        fov = 62;
        posRate = 60;
        targetRate = 60;
        break;
      }
      case 'cinematic':
      case 'wide':
      case 'detail': {
        const shots = this.mode === 'cinematic' ? CINEMATIC : this.mode === 'wide' ? WIDE : DETAIL;
        this.shotTime += dt;
        let shot = shots[this.shotIndex % shots.length]!;
        if (this.shotTime > shot.duration) {
          this.shotTime = 0;
          this.shotIndex++;
          shot = shots[this.shotIndex % shots.length]!;
          this.snap = true;
        }
        const r = shot.eval(this.shotTime, { ship, world, seed: this.shotIndex }, this.pos, this.target);
        fov = r.fov;
        focus = r.focus ?? this.pos.distanceTo(this.target);
        dof = r.dof ?? 0.2;
        this.shotName = shot.name;
        // Handheld drift.
        const t = world.time;
        const amp = this.mode === 'detail' ? 0.04 : this.mode === 'wide' ? 0.5 : 0.25;
        this.pos.x += fbm1(t * 0.3, 1) * amp;
        this.pos.y += fbm1(t * 0.27, 2) * amp;
        this.pos.z += fbm1(t * 0.31, 3) * amp;
        rollFollow = this.mode === 'detail' ? 0.4 : 0.05;
        posRate = this.mode === 'detail' ? 12 : 3.5;
        targetRate = this.mode === 'detail' ? 12 : 4;
        if (this.mode === 'detail') stabilise = 0.3;
        break;
      }
    }

    // Keep out of the sea.
    if (this.mode !== 'deck' && this.mode !== 'helm') {
      const h = world.waves.heightAt(this.pos.x, this.pos.z);
      this.pos.y = Math.max(this.pos.y, h + 1.0);
      if (this.mode === 'free') this.freePos.y = Math.max(this.freePos.y, h + 1.0);
    }

    // Weighty smoothing.
    if (this.snap) {
      this.smoothPos.copy(this.pos);
      this.smoothTarget.copy(this.target);
    } else {
      this.smoothPos.lerp(this.pos, 1 - Math.exp(-dt * posRate));
      this.smoothTarget.lerp(this.target, 1 - Math.exp(-dt * targetRate));
    }
    this.fov += (fov - this.fov) * (this.snap ? 1 : 1 - Math.exp(-dt * 3));
    this.focus += (focus - this.focus) * (this.snap ? 1 : 1 - Math.exp(-dt * 4));
    this.dofAmount += (dof - this.dofAmount) * (this.snap ? 1 : 1 - Math.exp(-dt * 3));

    const cam = this.camera;
    cam.position.copy(this.smoothPos);
    // Up vector: world up, blended toward the ship's up for on-board views.
    const shipUp = tmpV.set(0, 1, 0).applyQuaternion(ship.getWorldQuaternion(tmpQ));
    const up = new THREE.Vector3().copy(UP).lerp(shipUp, (1 - stabilise) + rollFollow).normalize();
    cam.up.copy(up);
    cam.lookAt(this.smoothTarget);

    // Shake (cannon, slams, thunder).
    this.shakeTime += dt;
    this.shakeAmp *= Math.exp(-dt * 3.5);
    if (this.shakeAmp > 0.001) {
      const s = this.shakeAmp;
      const t = this.shakeTime * 18;
      cam.rotateX(fbm1(t, 7) * 0.012 * s);
      cam.rotateY(fbm1(t, 8) * 0.012 * s);
      cam.rotateZ(fbm1(t, 9) * 0.008 * s);
    }
    cam.fov = this.fov;
    cam.updateProjectionMatrix();
    cam.updateMatrixWorld();
    this.snap = false;
  }
}
